import { OAuthCredentialsService } from '@tableau/oauth-credentials-service';
import { ButtonType, ButtonWidget, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { Filter } from '~src/commonInfra/utils/Filter';
import { OAuthCredentials } from '~src/connectivity/resources/OAuthCredentials';
import { IOAuthCredential, OAuthCredentialTypes } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { SalesforceIntegrationAddCredentialsButtonClickEvent } from '~src/settings/IntegrationsSettings/telemetry/IntegrationsSettingsTelemetry';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { UploadBundlesDialogOpts } from '~src/uploadBundles/components/ConnectedUploadBundlesDialog';

import ConnectToSalesforceDropdown, { ConnectToSalesforce } from './components/ConnectToSalesforceDropdown';
import { integrationSettingConnectButton, integrationSpaceAuthenticateContainer, integrationsSettingsPageHeader, salesforceSettingsAfterConnecting, salesforceSettingsGroupName, settingsGroupContainer, settingsSubsection } from './styles/IntegrationsSettingsStyles';

export interface PublishSalesforceBundlesSettingsState {
    readonly credentials: IOAuthCredential[];
}

export interface PublishSalesforceBundlesSettingsProps {
    readonly server: SessionManagementService;
    readonly urls: SpacesSiteUrls;
    readonly isOnlineEnvironment: boolean;
}
export const learnMoreTokenScheduleRefreshes = 'schedule-refreshes';
export const learnMoreTokenConnectToSalesforce = 'server-connect-salesforce';
export class PublishSalesforceBundlesSettings extends React.Component<PublishSalesforceBundlesSettingsProps, PublishSalesforceBundlesSettingsState> {

    public constructor(props: PublishSalesforceBundlesSettingsProps) {
        super(props);
        this.state = {
            credentials: null,
        };
    }

    public async componentDidMount() {
        await this.getOauthCredentials();
    }

    render() {
        return (<div>
            <h2 className={integrationsSettingsPageHeader}>{ Messages.connectToSalesforce_SalesforceDataSourcesAndWorkbooksHeader()}</h2>
            { this.connectToSalesforceSegment() }
            { this.authenticationSegment() }
          </div>);
    }

    async getOauthCredentials() {
        const credentialsQuery: ResourceQuery<IOAuthCredential> =
            OAuthCredentials.all().addFilter(Filter.fieldEq('type', OAuthCredentialTypes.Salesforce));

        const result = await SliceableHelper.fetchAll(credentialsQuery);
        if (_.isEmpty(result.errors)) {
            this.setState({ credentials: result.items });
        }
    }

    public handleSelectedCredentials = (credentialId: number): boolean => {
        ReactModalService.openCustom(UploadBundlesDialogOpts(credentialId));
        return true;
    };

    public handleConnectToSalesforce = (): void => {
        SalesforceIntegrationAddCredentialsButtonClickEvent();
        OAuthCredentialsService.AddCredential({
            bypassBeforeConnectingDialog: true,
            connectionType: OAuthCredentialTypes.Salesforce,
            onCancel: () => {
            }, // TODO: probably dont need to do anything.
            onCredentialAdded: this.handleOnCredentialsAdded,
            shouldKeepWindowOpen: false,
            sendEventFn: () => {
            },
            sessionLanguage: this.props.server.sessionLanguage(),
            sessionServerVersionBuild: this.props.server.sessionServerVersion().build,
            sessionServerVersionPlatform: this.props.server.sessionServerVersion().platform,
            connectionTypeDisplay: Messages.salesforce(),
            helpServiceExternalVersion: undefined,
            supportsGenericAuth: false,
        });
    };

    // credentialProperties is needed because we use it as a callback that requires it.
    handleOnCredentialsAdded = async (credentialId: string, _credentialProperties: [string, any]) => {
        this.handleSelectedCredentials(parseInt(credentialId));
        await this.getOauthCredentials(); // refresh state so button migrates to dropdown.
    };

    private scheduleRefreshesTextWidget = (): JSX.Element => {
        return <TextLinkWidget style={{ display: 'inline-block' }}
            testId={`${ConnectToSalesforce}-schedule-refreshes-learn-more`}
            kind={TextLinkKind.Standard}
            href={HelpService.getHelpUrlByCategory(learnMoreTokenScheduleRefreshes)}
            target={'_blank'}
            rel={'noreferrer noopener'}>
            {Messages.connectToSalesforce_LearnMore()}
        </TextLinkWidget>;
    };


    public connectToSalesforceSegment = (): JSX.Element => {
        return <div className={settingsGroupContainer}>
            {<p className={settingsSubsection}>
                {Messages.connectToSalesforce_DataSourcesAndWorkbooksExploreData()}
                <TextLinkWidget
                    testId={`${ConnectToSalesforce}-create-workbooks-learn-more`}
                    kind={TextLinkKind.Standard}
                    href={HelpService.getHelpUrlByCategory(learnMoreTokenConnectToSalesforce)}
                    target={'_blank'}
                    rel={'noreferrer noopener'}>
                    {Messages.connectToSalesforce_LearnMore()}
                </TextLinkWidget>
            </p>}
            <h3 className={salesforceSettingsGroupName}>
                {Messages.connectToSalesforce_getStarted()}
            </h3>
            <p className={settingsSubsection}>
                {Messages.connectToSalesforce_DataSourcesAndWorkbooksParagraph()}
            </p>
            <p className={settingsSubsection}>
                {Messages.connectToSalesforce_DataSourceAndWorkbooksEmbedCredentials()}
            </p>
            {this.getButton(this.state.credentials)}
            <h3 className={salesforceSettingsAfterConnecting}>
                {Messages.connectToSalesforce_afterConnecting()}
            </h3>
            <div className={settingsSubsection} style={{ paddingBottom: 0 }}>
                <ul style={{ paddingLeft: 22, paddingBottom: 0 }}>
                    <li>{Messages.connectToSalesforce_DataSourcesAndWorkbooksAfterConnectingBulletOne()}</li>
                    <li style={{ whiteSpace: 'nowrap' }}>{Messages.connectToSalesforce_DataSourcesAndWorkbooksAfterConnectingBulletTwo()}
                        {this.scheduleRefreshesTextWidget()}
                    </li>
                </ul>
            </div>
        </div>;
    };

    public getButton = (credentials: IOAuthCredential[]): JSX.Element => {
        if (credentials && (credentials.length > 0)) {
            return <ConnectToSalesforceDropdown
                credentials={credentials}
                handleSelectedItem={this.handleSelectedCredentials}
                handleAddNewCredentials={this.handleConnectToSalesforce}
            />;
        }

        return <>
            <ButtonWidget
                testId={`${ConnectToSalesforce}-data`}
                className={integrationSettingConnectButton}
                id={`${ConnectToSalesforce}-data-button`}
                handleClick={this.handleConnectToSalesforce}
                buttonType={ButtonType.Outline}
            >
                { Messages.connectToSalesforce_Title()}
            </ButtonWidget>
        </>;
    };

    public authenticationSegment = (): JSX.Element => {
        if (this.props.isOnlineEnvironment) {
            return <div>
                <div className={integrationSpaceAuthenticateContainer}>
                    <h2 className={integrationsSettingsPageHeader}>
                        {Messages.connectToSalesforce_AuthenticationHeader()}
                    </h2>
                </div>
                <div className={settingsGroupContainer}>
                    {this.authenticationBody()}
                </div>
            </div>;
        }
    };

    private authenticationBody = (): JSX.Element => {
        return <p className={settingsSubsection}>
            {Messages.connectToSalesforce_AuthenticationBody()}
            <TextLinkWidget
                testId={`${ConnectToSalesforce}-authentication`}
                kind={TextLinkKind.Standard}
                target={'_self'}
                href={this.props.urls.settingsAuthenticationUrl()}
            >
                {Messages.connectToSalesforce_AuthenticationLink()}
            </TextLinkWidget>
        </p>;
    };
}